import React, { Component } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import {
  Button,
  GenericInput,
  CheckBox,
  RadioButton,
  OverlayPage,
  OverlayPageContents,
  OverlayPageSection,
  OverlayPageBottomButtons,
  UserListing,
  SuccessPopup,
  Tag,
  Popup,
} from '../../components';
import { feedbackActions, userActions } from '../../webapi';
import { checkLoggedIn, validateAccess } from '../../session';
import { safeReadParams } from '../../helper';
import { pollsLoaded } from '../../actions';
import { Text } from '../../components/text';

class SubmitPoll extends Component {
  initialState = {
    pollId: safeReadParams(this.props, 'pollId'),
    poll: {},

    // User selection state (following AddJob pattern)
    selectedUser: null,
    userFilterOpen: false,
    userSearch: '',
    users: [],
    loadingUsers: false,

    // Anonymous submission option
    isAnonymous: false,
    anonymousName: '',

    // Form state
    answers: {},
    submitting: false,
    success: false,
    errorMessage: '',
    showWarnings: false,
    pollLoaded: false,
    loadedPolls: false,
  };

  state = { ...this.initialState };

  UNSAFE_componentWillMount() {
    checkLoggedIn(this, this.props.auth);
    this.setStatePoll(this.props.polls, safeReadParams(this.props, 'pollId'));
  }

  componentDidMount() {
    setTimeout(() => {
      if (!validateAccess(this.props.auth.site, 'pollsSubmit', this.props.auth)) {
        window.history.back();
      }
    }, 500);

    this.loadUsers();
  }

  setStatePoll(polls, pollId) {
    const poll = _.find(polls, (p) => {
      return p != null && p.Id === pollId;
    });

    if (poll) {
      this.setState({
        poll,
        pollLoaded: true,
        answers: this.initializeAnswers(poll.Questions),
      });
    } else if (!this.state.loadedPolls) {
      this.loadPolls();
    }
  }

  loadPolls() {
    this.setState({ loadedPolls: true });
    feedbackActions.getPolls(this.props.auth.site).then((res) => {
      if (res.data != null && !_.isEmpty(res.data) && res.data[0].Site === this.props.auth.site) {
        this.props.pollsLoaded(res.data);
      }
      this.setStatePoll(res.data, this.state.pollId);
    });
  }

  loadUsers() {
    this.setState({ loadingUsers: true });
    userActions
      .fetchUsers(this.props.auth.site)
      .then((res) => {
        if (res.data && res.data.results && res.data.results.Items) {
          const users = res.data.results.Items.filter((user) => user.Id !== this.props.auth.user.Id).map((user) => ({
            ...user,
            displayName: user.displayName || user.name || `User ${user.Id}`,
          }));
          this.setState({
            users,
            loadingUsers: false,
          });
        } else {
          this.setState({ loadingUsers: false });
        }
      })
      .catch((error) => {
        console.error('Error loading users:', error);
        this.setState({ loadingUsers: false });
      });
  }

  initializeAnswers(questions) {
    const answers = {};
    questions.forEach((q) => {
      if (q.Type === 'checkbox') {
        answers[q.Id] = [];
      } else {
        answers[q.Id] = '';
      }
    });
    return answers;
  }

  // User selection methods (following AddJob pattern)
  onOpenUserSelector = () => {
    this.setState({ userFilterOpen: true });
  };

  onCloseUserSelector = () => {
    this.setState({ userFilterOpen: false });
  };

  onSelectUser = (user) => {
    this.setState({
      selectedUser: user,
      userFilterOpen: false,
      isAnonymous: false, // Clear anonymous if user is selected
    });
  };

  onUnselectUser = () => {
    this.setState({ selectedUser: null });
  };

  // Form handling methods
  onHandleChange = (e) => {
    const { id, value } = e.target;
    this.setState({ [id]: value });
  };

  handleAnswerChange = (questionId, answer) => {
    this.setState({
      answers: {
        ...this.state.answers,
        [questionId]: answer,
      },
    });
  };

  handleMultiChoiceChange = (questionId, optionValue) => {
    const currentAnswer = this.state.answers[questionId];
    const newAnswer = currentAnswer === optionValue ? undefined : optionValue;
    this.handleAnswerChange(questionId, newAnswer);
  };

  handleCheckboxChange = (questionId, optionValue) => {
    const currentAnswers = this.state.answers[questionId] || [];
    const newAnswers = _.xor(currentAnswers, [optionValue]);
    this.handleAnswerChange(questionId, newAnswers);
  };

  handleAnonymousToggle = () => {
    this.setState((prevState) => ({
      isAnonymous: !prevState.isAnonymous,
      selectedUser: !prevState.isAnonymous ? null : prevState.selectedUser, // Clear user if anonymous
    }));
  };

  getFieldContainerClass = (isValid = true) => {
    const showError = this.state.showWarnings && !isValid;
    return `genericInputContainer ${isValid ? 'genericInput-valid' : ''} ${showError ? 'genericInput-error' : ''}`.trim();
  };

  validateMultiChoice(q) {
    const answer = this.state.answers[q.Id];
    return !_.isUndefined(answer) && !_.isNull(answer) && answer !== '';
  }

  validateParagraph(q) {
    return !_.isEmpty(this.state.answers[q.Id]);
  }

  validateScale(q) {
    const answer = this.state.answers[q.Id];
    return !_.isUndefined(answer) && !_.isNull(answer) && answer !== '';
  }

  validateCheckboxes(q) {
    return !_.isEmpty(this.state.answers[q.Id]);
  }

  validateQuestion(q) {
    switch (q.Type) {
      case 'multichoice':
        return this.validateMultiChoice(q);
      case 'paragraph':
        return this.validateParagraph(q);
      case 'scale':
        return this.validateScale(q);
      case 'checkbox':
        return this.validateCheckboxes(q);
      default:
        // Handle text and other input types
        return !_.isEmpty(this.state.answers[q.Id]);
    }
  }

  validateForm = () => {
    const { poll, selectedUser, isAnonymous, anonymousName } = this.state;

    // Check user/anonymous selection
    if (!isAnonymous && !selectedUser) {
      return false;
    }

    if (isAnonymous && !anonymousName.trim()) {
      return false;
    }

    // Check required answers using question-specific validators
    let anyNotReady = false;
    poll.Questions.forEach((q) => {
      if (!this.validateQuestion(q)) {
        anyNotReady = true;
      }
    });

    return !anyNotReady;
  };

  handleSubmit = async () => {
    this.setState({ showWarnings: true });

    if (!this.validateForm()) {
      this.setState({ errorMessage: 'Please complete all required fields.' });
      return;
    }

    this.setState({ submitting: true, errorMessage: '' });

    try {
      const { poll, selectedUser, isAnonymous, anonymousName, answers } = this.state;

      let requestData = {
        id: poll.Id,
        answers: answers,
      };

      // Add admin-specific parameters based on selection (PC-1261 extensions)
      if (isAnonymous) {
        requestData.anonymousName = anonymousName;
      } else if (selectedUser) {
        requestData.onBehalfOf = selectedUser.userId || selectedUser.id;
      }

      await feedbackActions.submitAnswer(requestData);

      this.setState({
        success: true,
        submitting: false,
      });
    } catch (error) {
      console.error('Admin submission error:', error);
      this.setState({
        submitting: false,
        errorMessage:
          (error.response && error.response.data && error.response.data.message) || 'Failed to submit survey response. Please try again.',
      });
    }
  };

  // Render methods
  renderSelectUser() {
    const { showWarnings, selectedUser, isAnonymous, anonymousName } = this.state;

    const isUserSelectionValid = isAnonymous || !_.isNil(selectedUser);
    const isAnonymousValid = !isAnonymous || !_.isEmpty(anonymousName);
    const isValid = isUserSelectionValid && isAnonymousValid;

    return (
      <div>
        {/* User label */}
        <Text type="formLabel" className="marginBottom-8">
          User
        </Text>

        {/* User selection - always visible */}
        <div className={this.getFieldContainerClass(isAnonymous || !_.isNil(selectedUser))}>
          <div style={styles.fieldContainer}>
            <div className="inputRequired " />
            {selectedUser ? (
              <Tag className="marginRight-10" rightIcon="close" rightClick={this.onUnselectUser}>
                <UserListing size={15} user={selectedUser} textClass="tag_text" />
              </Tag>
            ) : (
              <Tag onClick={this.onOpenUserSelector} text="Select User" />
            )}
          </div>
        </div>

        {/* Anonymous checkbox */}
        <div style={styles.checkboxContainer}>
          <CheckBox
            isActive={isAnonymous}
            onChange={this.handleAnonymousToggle}
            label="This survey response is not associated with a user on the platform"
          />
        </div>

        {/* Anonymous name input - only show when checkbox is checked */}
        {isAnonymous && (
          <div className={this.getFieldContainerClass(!_.isEmpty(anonymousName))}>
            <GenericInput
              id="anonymousName"
              type="text"
              placeholder="Enter a name"
              value={anonymousName}
              onChange={this.onHandleChange}
              isRequired
              isValid={() => !_.isEmpty(anonymousName)}
              showError={() => showWarnings && _.isEmpty(anonymousName)}
            />
          </div>
        )}

        {/* Show validation warning */}
        {showWarnings && !isValid && (
          <div className="fieldLabel fieldLabel-warning marginTop-8">Please select a user or check the box above and enter a name</div>
        )}
      </div>
    );
  }

  renderUserFilterPopup() {
    const { userFilterOpen, userSearch, users, loadingUsers } = this.state;

    if (!userFilterOpen) return null;

    return (
      <Popup
        title="Select User"
        maxWidth={600}
        minWidth={400}
        maxHeight={600}
        minHeight={600}
        hasPadding
        onClose={this.onCloseUserSelector}
        buttons={[
          {
            type: 'tertiary',
            onClick: this.onCloseUserSelector,
            isActive: true,
            text: 'Cancel',
          },
        ]}
      >
        <GenericInput
          id="userSearch"
          type="text"
          label="Search User"
          placeholder="Search name"
          value={userSearch}
          onChange={this.onHandleChange}
          alwaysShowLabel
        />
        {users.length === 0 ? (
          <Text type="body" className="marginTop-16">
            {loadingUsers ? 'Loading users...' : 'No users found'}
          </Text>
        ) : (
          _.sortBy(users, (u) => (u.displayName || u.name || '').toUpperCase())
            .filter((u) => {
              if (_.isEmpty(userSearch)) return true;
              const searchName = (u.displayName || u.name || '').toUpperCase();
              return searchName.indexOf(userSearch.toUpperCase()) > -1;
            })
            .map((user) => {
              return <UserListing key={user.userId || user.id} user={user} onClick={() => this.onSelectUser(user)} />;
            })
        )}
      </Popup>
    );
  }

  renderSurveyQuestions() {
    const { poll } = this.state;

    if (!poll.Questions || poll.Questions.length === 0) {
      return <Text type="body">No questions available for this survey.</Text>;
    }

    return poll.Questions.map((question, index) => {
      const isValid = this.validateQuestion(question);
      return (
        <div key={question.Id} className={this.getFieldContainerClass(isValid)} style={styles.questionContainerWrapper}>
          <div className="inputRequired " />
          <div className="visitorSignIn_question" style={styles.questionContainer}>
            <Text type="formLabel" className="marginBottom-8">
              {question.Text}
            </Text>

            {this.renderQuestionInput(question)}
          </div>
        </div>
      );
    });
  }

  renderQuestionInput(question) {
    const { answers } = this.state;
    const currentAnswer = answers.hasOwnProperty(question.Id) ? answers[question.Id] : '';

    switch (question.Type) {
      case 'multichoice':
        return (
          <div style={styles.optionContainer}>
            <RadioButton
              label=""
              isActive={currentAnswer}
              options={question.Options.map((option) => ({
                Label: option.Text,
                Value: option.Id,
                onChange: () => this.handleMultiChoiceChange(question.Id, option.Id),
              }))}
              rowStyle={{ flexDirection: 'column' }}
              buttonStyle={{ marginTop: '5px', marginBottom: '5px' }}
            />
          </div>
        );

      case 'checkbox':
        return question.Options.map((option, optionIndex) => (
          <div key={optionIndex} style={styles.optionContainer}>
            <CheckBox
              key={optionIndex}
              label={option.Text}
              isActive={(currentAnswer || []).includes(option.Id)}
              onChange={() => this.handleCheckboxChange(question.Id, option.Id)}
            />
          </div>
        ));

      case 'scale':
        return (
          <div style={styles.optionContainer}>
            <RadioButton
              label=""
              isActive={currentAnswer}
              options={question.Options.map((option) => ({
                Label: `${option.Value}: ${option.Text}`,
                Value: option.Value,
                onChange: () => this.handleMultiChoiceChange(question.Id, option.Value),
              }))}
              rowStyle={{ flexDirection: 'column' }}
              buttonStyle={{ marginTop: '5px', marginBottom: '5px' }}
            />
          </div>
        );

      case 'paragraph':
        return (
          <GenericInput
            id={`question-${question.Id}`}
            type="textarea"
            placeholder={question.Text}
            value={currentAnswer}
            onChange={(e) => this.handleAnswerChange(question.Id, e.target.value)}
            inputStyle={{ height: 100, marginTop: 8 }}
          />
        );

      default:
        return (
          <GenericInput
            id={`question-${question.Id}`}
            type="text"
            placeholder={question.Text}
            value={currentAnswer}
            onChange={(e) => this.handleAnswerChange(question.Id, e.target.value)}
            inputStyle={{ marginTop: 8 }}
          />
        );
    }
  }

  renderSubmit() {
    const { submitting } = this.state;

    return (
      <div style={styles.buttonContainer}>
        <Button inline buttonType="tertiary" isActive onClick={() => window.history.back()} disabled={submitting}>
          Cancel
        </Button>
        <Button inline buttonType="primary" onClick={this.handleSubmit} isActive={this.validateForm()} disabled={submitting}>
          {submitting ? 'Saving...' : 'Save'}
        </Button>
      </div>
    );
  }

  render() {
    const { poll, success, errorMessage } = this.state;

    if (!poll.Id) {
      return (
        <OverlayPage title="Loading..." onClose={() => window.history.back()}>
          <OverlayPageContents>
            <OverlayPageSection className="pageSectionWrapper--newPopup">
              <div className="padding-60 paddingVertical-40">
                <Text type="body">Loading survey...</Text>
              </div>
            </OverlayPageSection>
          </OverlayPageContents>
        </OverlayPage>
      );
    }

    return (
      <OverlayPage title={`Submit Response: ${poll.Title}`} onClose={() => window.history.back()}>
        <OverlayPageContents noBottomButtons={success}>
          <OverlayPageSection className="pageSectionWrapper--newPopup">
            <div className="padding-60 paddingVertical-40">
              {success && (
                <SuccessPopup
                  text="Survey response has been submitted"
                  buttons={[
                    {
                      type: 'outlined',
                      onClick: () => {
                        window.location.href = `/polls/results/${poll.Id}`;
                      },
                      text: 'View Results',
                    },
                  ]}
                />
              )}

              {!success && (
                <div>
                  {errorMessage && <div className="errorMessage marginBottom-16">{errorMessage}</div>}

                  <Text type="h3" className="marginBottom-16">
                    {poll.Title}
                  </Text>
                  {this.renderSelectUser()}
                  {this.renderSurveyQuestions()}
                </div>
              )}
            </div>
            {this.renderUserFilterPopup()}
          </OverlayPageSection>
        </OverlayPageContents>
        {!success && <OverlayPageBottomButtons>{this.renderSubmit()}</OverlayPageBottomButtons>}
      </OverlayPage>
    );
  }
}

const styles = {
  userLabelContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    justifyContent: 'space-between',
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    position: 'relative',
  },
  checkboxContainer: {
    marginTop: 16,
    marginBottom: 16,
  },
  separator: {
    height: 1,
    backgroundColor: '#e0e0e0',
    margin: '24px 0',
  },
  questionContainerWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  questionContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: 12,
  },
  optionContainer: {
    marginTop: 8,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
};

const mapStateToProps = (state) => {
  const { polls } = state.feedback;
  const { auth } = state;
  return { polls, auth };
};

export default connect(mapStateToProps, { pollsLoaded })(SubmitPoll);
